import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Shield, AlertCircle, CheckCircle, Loader2, AlertTriangle, Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import AddressSection from '../components/AddressSection';
import { format } from 'date-fns';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Minimum age requirement
const MINIMUM_AGE = 30;

// Generate year options (from 100 years ago to minimum age years ago)
const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear - MINIMUM_AGE;
  const minYear = currentYear - 100;
  const years = [];
  for (let year = maxYear; year >= minYear; year--) {
    years.push(year);
  }
  return years;
};

// Generate month options
const MONTHS = [
  { value: 0, label: 'January' },
  { value: 1, label: 'February' },
  { value: 2, label: 'March' },
  { value: 3, label: 'April' },
  { value: 4, label: 'May' },
  { value: 5, label: 'June' },
  { value: 6, label: 'July' },
  { value: 7, label: 'August' },
  { value: 8, label: 'September' },
  { value: 9, label: 'October' },
  { value: 10, label: 'November' },
  { value: 11, label: 'December' }
];

// Calculate age from date of birth
const calculateAge = (dob) => {
  if (!dob) return null;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Get max date for DOB (must be at least 30 years old)
const getMaxDobDate = () => {
  const today = new Date();
  today.setFullYear(today.getFullYear() - MINIMUM_AGE);
  return today;
};

// Format phone number as (xxx) xxx-xxxx
const formatPhoneNumber = (value) => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Format based on length
  if (digits.length <= 3) {
    return digits.length > 0 ? `(${digits}` : '';
  } else if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
};

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

export default function PolicyholderInfoPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [ageError, setAgeError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date(1990, 0, 1)); // Start viewing from 1990

  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    mailing_address: {
      street: '',
      apartment: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'United States'
    },
    garaging_same_as_mailing: true,
    garaging_address: {
      street: '',
      apartment: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'United States'
    },
    registration_state: '',
    registration_same_as_mailing: true,
    registration_address: {
      street: '',
      apartment: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'United States'
    },
    drivers_license_state: '',
    drivers_license_number: '',
    co_insured_name: '',
    is_business_use: false,
    info_accurate_confirmed: false,
    vehicle_registration_confirmed: false,
    state_notices_acknowledged: false,
    inaccurate_info_acknowledged: false
  });

  // State-specific surplus lines disclosures (exact regulatory language)
  const SURPLUS_LINES_DISCLOSURES = {
    AZ: {
      state_name: "Arizona",
      disclosure_text: "This policy is issued by a nonadmitted insurer that is not subject to the financial conditions or regulation of the Arizona Department of Insurance and Financial Institutions. This policy is not protected by the Arizona Property and Casualty Insurance Guaranty Fund."
    },
    CA: {
      state_name: "California",
      disclosure_text: "This insurance contract is issued by a nonadmitted insurer that is not licensed by the State of California. The insurer is not subject to the financial solvency regulation and enforcement that apply to California licensed insurers. This policy is not protected by the California Insurance Guarantee Association."
    },
    CO: {
      state_name: "Colorado",
      disclosure_text: "This insurance contract is issued by a nonadmitted insurer not licensed in Colorado. The insurer is not subject to regulation by the Colorado Division of Insurance, and this policy is not covered by the Colorado Insurance Guaranty Association."
    },
    DE: {
      state_name: "Delaware",
      disclosure_text: "This insurance contract is issued pursuant to the Delaware Insurance Laws by an insurer neither licensed by nor under the jurisdiction of the Delaware Insurance Department. This insurer does not participate in insurance guaranty funds created by state law. In the event of the insolvency of the surplus lines insurer, losses will not be paid by the Delaware Insurance Guaranty Association."
    },
    FL: {
      state_name: "Florida",
      disclosure_text: "This insurance is issued pursuant to the Florida Surplus Lines Law. Persons insured by surplus lines carriers do not have the protection of the Florida Insurance Guaranty Act to the extent of any right of recovery for the obligation of an insolvent unlicensed insurer. Additionally, surplus lines insurers' policy rates and forms are not approved by any Florida regulatory agency."
    },
    GA: {
      state_name: "Georgia",
      disclosure_text: "The insurance company with which this coverage has been placed is not licensed in this state but is an eligible surplus lines insurer. The company is not regulated by the Georgia Insurance Department and is not protected by any state insolvency or guaranty fund."
    },
    LA: {
      state_name: "Louisiana",
      disclosure_text: "This insurance contract is issued by a nonadmitted insurer that is not licensed by the State of Louisiana. The insurer is not subject to the Louisiana Insurance Guaranty Association, and policyholders are therefore not protected by the Association in the event of insolvency of the insurer."
    },
    MA: {
      state_name: "Massachusetts",
      disclosure_text: "This insurance contract is issued by a surplus lines insurer not licensed in Massachusetts. The insurer is not subject to supervision by the Massachusetts Division of Insurance. This policy is not protected by the Massachusetts Insurers Insolvency Fund."
    },
    MD: {
      state_name: "Maryland",
      disclosure_text: "This insurance contract is issued by a surplus lines insurer not licensed in Maryland. The insurer is not subject to regulation by the Maryland Insurance Administration. This policy is not protected by the Maryland Property and Casualty Insurance Guaranty Corporation."
    },
    MI: {
      state_name: "Michigan",
      disclosure_text: "This insurance contract is issued by a surplus lines insurer not licensed in Michigan. The insurer is not subject to supervision by the Michigan Department of Insurance and Financial Services. This policy is not protected by the Michigan Property and Casualty Guaranty Association."
    },
    NC: {
      state_name: "North Carolina",
      disclosure_text: "This insurance contract is issued by a nonadmitted company that is not licensed by the State of North Carolina. The insurer is not subject to the supervision of the North Carolina Department of Insurance, and in case of insolvency, the policy is not protected by the North Carolina Insurance Guaranty Association."
    },
    NV: {
      state_name: "Nevada",
      disclosure_text: "This insurance contract is issued by a nonadmitted insurer not licensed by the State of Nevada. The insurer does not participate in any Nevada insurance guaranty fund, and the policy is not protected by such funds."
    },
    NY: {
      state_name: "New York",
      disclosure_text: "This insurance contract is issued by a surplus lines insurer not licensed in New York. The insurer is not subject to supervision by the New York State Department of Financial Services. This policy is not protected by the New York State Security Fund."
    },
    TX: {
      state_name: "Texas",
      disclosure_text: "This insurance contract is with an insurer not licensed to transact insurance in this state and is issued and delivered as surplus line coverage under the Texas insurance statutes. The Texas Department of Insurance does not audit the finances or review the solvency of the surplus lines insurer providing this coverage, and the insurer is not a member of the property and casualty insurance guaranty association created under Chapter 462, Insurance Code."
    },
    VA: {
      state_name: "Virginia",
      disclosure_text: "This insurance contract is issued by a nonadmitted insurer not licensed by the State Corporation Commission of Virginia. The insurer is not subject to Virginia's insurance laws and regulations, and this policy is not protected by the Virginia Property and Casualty Insurance Guaranty Association."
    },
    WA: {
      state_name: "Washington",
      disclosure_text: "This contract of insurance is issued by a nonadmitted insurer that is not licensed by the State of Washington. The insurer is not subject to the regulation of the Washington Office of the Insurance Commissioner, and this policy is not protected by the Washington Insurance Guaranty Association."
    }
  };

  // Get the controlling state for disclosure display
  const getControllingState = () => {
    if (formData.registration_state) return formData.registration_state;
    if (!formData.garaging_same_as_mailing && formData.garaging_address?.state) {
      return formData.garaging_address.state;
    }
    return formData.mailing_address?.state || '';
  };

  const controllingState = getControllingState();
  const surplusLinesDisclosure = SURPLUS_LINES_DISCLOSURES[controllingState];

  // Check issuance status on mount
  useEffect(() => {
    const checkStatus = async () => {
      if (!sessionId) {
        navigate('/');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/policy/status/${sessionId}`);
        const data = await response.json();

        if (data.status === 'issued' && data.policy_number) {
          // Policy already issued, redirect to confirmation
          navigate(`/confirmation?session_id=${sessionId}`);
        } else if (data.status === 'payment_pending') {
          // Payment not complete, redirect to success page to check
          navigate(`/success?session_id=${sessionId}`);
        }
        // If status is 'pending_info', stay on this page (allow form submission)
      } catch (error) {
        console.error('Error checking status:', error);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkStatus();
  }, [sessionId, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Validate age when DOB changes
    if (field === 'date_of_birth' && value) {
      const age = calculateAge(value);
      if (age !== null && age < MINIMUM_AGE) {
        setAgeError(`You are not eligible to enroll. LossPay coverage is available only to applicants who are ${MINIMUM_AGE} years of age or older.`);
      } else {
        setAgeError('');
      }
    }
  };

  const handleAddressChange = (addressType, field, value) => {
    setFormData(prev => ({
      ...prev,
      [addressType]: {
        ...prev[addressType],
        [field]: value
      }
    }));
  };

  // Client-side field validation
  const validateForm = () => {
    const errs = {};

    if (!formData.first_name.trim()) errs.first_name = 'First name is required';
    if (!formData.last_name.trim()) errs.last_name = 'Last name is required';
    if (!formData.email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Enter a valid email address';
    if (!formData.phone.trim()) errs.phone = 'Phone number is required';
    else if (formData.phone.replace(/\D/g, '').length < 10) errs.phone = 'Enter a valid 10-digit phone number';
    if (!formData.date_of_birth) errs.date_of_birth = 'Date of birth is required';

    // Mailing address
    if (!formData.mailing_address.street.trim()) errs['mailing_address.street'] = 'Street address is required';
    if (!formData.mailing_address.city.trim()) errs['mailing_address.city'] = 'City is required';
    if (!formData.mailing_address.state.trim()) errs['mailing_address.state'] = 'State is required';
    if (!formData.mailing_address.zip_code.trim()) errs['mailing_address.zip_code'] = 'ZIP code is required';

    // Registration state
    if (!formData.registration_state) errs.registration_state = 'Registration state is required';

    // Garaging address (if different)
    if (!formData.garaging_same_as_mailing) {
      if (!formData.garaging_address.street.trim()) errs['garaging_address.street'] = 'Garaging street is required';
      if (!formData.garaging_address.city.trim()) errs['garaging_address.city'] = 'Garaging city is required';
      if (!formData.garaging_address.state.trim()) errs['garaging_address.state'] = 'Garaging state is required';
      if (!formData.garaging_address.zip_code.trim()) errs['garaging_address.zip_code'] = 'Garaging ZIP is required';
    }

    // Attestations
    if (!formData.info_accurate_confirmed) errs.info_accurate_confirmed = 'This acknowledgement is required';
    if (!formData.vehicle_registration_confirmed) errs.vehicle_registration_confirmed = 'This acknowledgement is required';
    if (!formData.state_notices_acknowledged) errs.state_notices_acknowledged = 'This acknowledgement is required';
    if (!formData.inaccurate_info_acknowledged) errs.inaccurate_info_acknowledged = 'This acknowledgement is required';

    return errs;
  };

  // Helper to get field error (supports nested keys like 'mailing_address.street')
  const getFieldError = (key) => attemptedSubmit ? fieldErrors[key] : null;

  // Clear individual field error when user edits it
  const clearFieldError = (key) => {
    if (fieldErrors[key]) {
      setFieldErrors(prev => { const n = {...prev}; delete n[key]; return n; });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAttemptedSubmit(true);
    setErrors([]);
    setWarnings([]);

    // Client-side validation
    const validationErrors = validateForm();
    setFieldErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      // Build summary of missing fields
      const errorMessages = Object.values(validationErrors);
      setErrors([`Please complete all required fields (${errorMessages.length} issue${errorMessages.length > 1 ? 's' : ''} found)`]);
      
      // Scroll to first error field
      const firstErrorKey = Object.keys(validationErrors)[0];
      const fieldId = firstErrorKey.replace('.', '-');
      const element = document.querySelector(`[data-field="${firstErrorKey}"], #${fieldId}, [id="${firstErrorKey}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    setLoading(true);

    // Check age eligibility before submission
    if (formData.date_of_birth) {
      const age = calculateAge(formData.date_of_birth);
      if (age !== null && age < MINIMUM_AGE) {
        setAgeError(`You are not eligible to enroll. LossPay coverage is available only to applicants who are ${MINIMUM_AGE} years of age or older.`);
        setLoading(false);
        return;
      }
    }

    try {
      const response = await fetch(`${API_URL}/api/policy/collect-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          policyholder: formData
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle age restriction error from backend
        if (data.detail && typeof data.detail === 'object' && data.detail.errors) {
          setErrors(data.detail.errors);
          if (data.detail.warnings) {
            setWarnings(data.detail.warnings);
          }
        } else {
          setErrors([data.detail || 'An error occurred']);
        }
        return;
      }

      if (data.validation && !data.validation.is_valid) {
        setErrors(data.validation.errors || []);
        setWarnings(data.validation.warnings || []);
        return;
      }

      if (data.validation?.warnings?.length > 0) {
        setWarnings(data.validation.warnings);
      }

      // Check if policy was issued or needs manual review
      if (data.status === 'issued' && data.policy_number) {
        navigate(`/confirmation?session_id=${sessionId}`);
      } else if (data.status === 'manual_review') {
        // For now, still go to confirmation with a review message
        navigate(`/confirmation?session_id=${sessionId}`);
      }
    } catch (error) {
      console.error('Error submitting info:', error);
      setErrors(['Failed to submit information. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Checking your policy status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <a href="https://losspay.com" target="_blank" rel="noopener noreferrer">
            <img 
              src="https://images.losspay.com/losspay/losspay-logo.png" 
              alt="LossPay" 
              className="h-8 md:h-10"
            />
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Complete Your Policy Details
          </h1>
          <p className="text-gray-600 max-w-xl mx-auto">
            We need a few final details to issue your coverage, apply the correct state notices, and finalize your policy documents.
          </p>
        </div>

        {/* Reassurance Note */}
        <Alert className="mb-6 bg-blue-50 border-blue-200">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            This information helps us confirm eligibility, ensure your vehicle is associated with the correct state, and protect against fraud.
          </AlertDescription>
        </Alert>

        {/* Error Messages */}
        {errors.length > 0 && (
          <Alert className="mb-6 bg-red-50 border-red-200" data-testid="form-errors-alert">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              <ul className="list-disc list-inside space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
              {attemptedSubmit && Object.keys(fieldErrors).length > 0 && (
                <p className="text-xs text-red-600 mt-2">Fields with errors are highlighted in red below.</p>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Warning Messages */}
        {warnings.length > 0 && (
          <Alert className="mb-6 bg-yellow-50 border-yellow-200">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              <ul className="list-disc list-inside space-y-1">
                {warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* Personal Information */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Personal Information</CardTitle>
              <CardDescription>Enter the named insured's legal information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div data-field="first_name">
                  <Label htmlFor="first_name">Legal First Name *</Label>
                  <Input
                    id="first_name"
                    data-testid="first-name-input"
                    value={formData.first_name}
                    onChange={(e) => { handleInputChange('first_name', e.target.value); clearFieldError('first_name'); }}
                    className={getFieldError('first_name') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    required
                  />
                  {getFieldError('first_name') && <p className="text-xs text-red-600 mt-1">{getFieldError('first_name')}</p>}
                </div>
                <div data-field="last_name">
                  <Label htmlFor="last_name">Legal Last Name *</Label>
                  <Input
                    id="last_name"
                    data-testid="last-name-input"
                    value={formData.last_name}
                    onChange={(e) => { handleInputChange('last_name', e.target.value); clearFieldError('last_name'); }}
                    className={getFieldError('last_name') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    required
                  />
                  {getFieldError('last_name') && <p className="text-xs text-red-600 mt-1">{getFieldError('last_name')}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div data-field="email">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="email-input"
                    value={formData.email}
                    onChange={(e) => { handleInputChange('email', e.target.value); clearFieldError('email'); }}
                    className={getFieldError('email') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    required
                  />
                  {getFieldError('email') && <p className="text-xs text-red-600 mt-1">{getFieldError('email')}</p>}
                </div>
                <div data-field="phone">
                  <Label htmlFor="phone">Mobile Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="phone-input"
                    value={formData.phone}
                    onChange={(e) => { handleInputChange('phone', formatPhoneNumber(e.target.value)); clearFieldError('phone'); }}
                    placeholder="(555) 555-5555"
                    maxLength={14}
                    className={getFieldError('phone') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    required
                  />
                  {getFieldError('phone') && <p className="text-xs text-red-600 mt-1">{getFieldError('phone')}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="max-w-xs" data-field="date_of_birth">
                  <Label htmlFor="dob">Date of Birth *</Label>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="dob"
                        variant="outline"
                        data-testid="dob-input"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.date_of_birth && "text-muted-foreground",
                          (ageError || getFieldError('date_of_birth')) && "border-red-500 focus:ring-red-500"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.date_of_birth ? (
                          format(new Date(formData.date_of_birth), "MM/dd/yyyy")
                        ) : (
                          <span>mm/dd/yyyy</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <div className="p-3 border-b">
                        <div className="flex items-center justify-between gap-2">
                          {/* Month Selector */}
                          <select
                            value={calendarMonth.getMonth()}
                            onChange={(e) => {
                              const newMonth = new Date(calendarMonth);
                              newMonth.setMonth(parseInt(e.target.value));
                              setCalendarMonth(newMonth);
                            }}
                            className="flex-1 h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                          >
                            {MONTHS.map((month) => (
                              <option key={month.value} value={month.value}>
                                {month.label}
                              </option>
                            ))}
                          </select>
                          {/* Year Selector */}
                          <select
                            value={calendarMonth.getFullYear()}
                            onChange={(e) => {
                              const newMonth = new Date(calendarMonth);
                              newMonth.setFullYear(parseInt(e.target.value));
                              setCalendarMonth(newMonth);
                            }}
                            className="w-24 h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                          >
                            {generateYearOptions().map((year) => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <Calendar
                        mode="single"
                        selected={formData.date_of_birth ? new Date(formData.date_of_birth) : undefined}
                        onSelect={(date) => {
                          if (date) {
                            const isoDate = format(date, "yyyy-MM-dd");
                            handleInputChange('date_of_birth', isoDate);
                            setCalendarOpen(false);
                          }
                        }}
                        month={calendarMonth}
                        onMonthChange={setCalendarMonth}
                        disabled={(date) => date > getMaxDobDate() || date < new Date("1924-01-01")}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  {/* Eligibility Notice */}
                  <p className="text-xs text-slate-500 mt-1">
                    <span className="font-medium">Eligibility Notice:</span> Applicants must be at least 30 years old to enroll in LossPay coverage.
                  </p>
                  {/* Age Error */}
                  {ageError && (
                    <p className="text-sm text-red-600 mt-2 flex items-start gap-2" data-testid="age-error">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{ageError}</span>
                    </p>
                  )}
                  {!ageError && getFieldError('date_of_birth') && <p className="text-xs text-red-600 mt-1">{getFieldError('date_of_birth')}</p>}
                </div>
                <div>
                  <Label htmlFor="co_insured">Co-Insured Name (if applicable)</Label>
                  <Input
                    id="co_insured"
                    data-testid="co-insured-input"
                    value={formData.co_insured_name}
                    onChange={(e) => handleInputChange('co_insured_name', e.target.value)}
                    placeholder="Joint owner or co-insured"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mailing Address */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Mailing Address</CardTitle>
              <CardDescription>Your primary mailing address - start typing to search and select</CardDescription>
            </CardHeader>
            <CardContent>
              <AddressSection
                address={formData.mailing_address}
                onAddressChange={(newAddress) => {
                  setFormData(prev => ({
                    ...prev,
                    mailing_address: newAddress
                  }));
                }}
                testIdPrefix="mailing-"
                enableValidation={true}
                showApartment={true}
                required={true}
              />
            </CardContent>
          </Card>

          {/* Vehicle Registration */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Vehicle Registration</CardTitle>
              <CardDescription>Where your vehicle is registered</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div data-field="registration_state">
                  <Label htmlFor="reg_state">Registration State *</Label>
                  <Select 
                    value={formData.registration_state}
                    onValueChange={(value) => { handleInputChange('registration_state', value); clearFieldError('registration_state'); }}
                  >
                    <SelectTrigger data-testid="registration-state-select" className={getFieldError('registration_state') ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {getFieldError('registration_state') && <p className="text-xs text-red-600 mt-1">{getFieldError('registration_state')}</p>}
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="garaging_same"
                  data-testid="garaging-same-checkbox"
                  checked={formData.garaging_same_as_mailing}
                  onCheckedChange={(checked) => handleInputChange('garaging_same_as_mailing', checked)}
                />
                <Label htmlFor="garaging_same" className="text-sm text-gray-600">
                  Vehicle is garaged at my mailing address
                </Label>
              </div>

              {!formData.garaging_same_as_mailing && (
                <div className="border-l-4 border-blue-200 pl-4 mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">Garaging Address</p>
                  <AddressSection
                    address={formData.garaging_address}
                    onAddressChange={(newAddress) => {
                      setFormData(prev => ({
                        ...prev,
                        garaging_address: newAddress
                      }));
                    }}
                    testIdPrefix="garaging-"
                    enableValidation={true}
                    showApartment={false}
                    required={true}
                  />
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="business_use"
                  data-testid="business-use-checkbox"
                  checked={formData.is_business_use}
                  onCheckedChange={(checked) => handleInputChange('is_business_use', checked)}
                />
                <Label htmlFor="business_use" className="text-sm text-gray-600">
                  This vehicle is used for commercial/business purposes
                </Label>
              </div>

              {/* Commercial Use Ineligibility Notice */}
              {formData.is_business_use && (
                <div 
                  data-testid="commercial-use-ineligible-notice"
                  className="mt-3 rounded-xl border border-red-200 bg-red-50 p-4 animate-fade-in"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-red-800 mb-1">Not Eligible for Coverage</h4>
                      <p className="text-sm text-red-700 leading-relaxed">
                        LossPay Diminished Value Protection is available for personal-use vehicles only. Vehicles used for commercial or business purposes are not eligible for coverage at this time.
                      </p>
                      <p className="text-xs text-red-600 mt-2">
                        If you believe this was selected in error, uncheck the box above to continue.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Required Attestations */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Required Acknowledgements</CardTitle>
              <CardDescription>Please confirm the following statements</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className={`flex items-start space-x-3 ${getFieldError('info_accurate_confirmed') ? 'p-3 rounded-lg bg-red-50 border border-red-200' : ''}`}>
                <Checkbox
                  id="attest1"
                  data-testid="attest-accurate-checkbox"
                  checked={formData.info_accurate_confirmed}
                  onCheckedChange={(checked) => { handleInputChange('info_accurate_confirmed', checked); clearFieldError('info_accurate_confirmed'); }}
                />
                <Label htmlFor="attest1" className={`text-sm leading-relaxed ${getFieldError('info_accurate_confirmed') ? 'text-red-700' : 'text-gray-700'}`}>
                  I confirm that the information I have provided is true, complete, and accurate.
                </Label>
              </div>
              
              <div className={`flex items-start space-x-3 ${getFieldError('vehicle_registration_confirmed') ? 'p-3 rounded-lg bg-red-50 border border-red-200' : ''}`}>
                <Checkbox
                  id="attest2"
                  data-testid="attest-registration-checkbox"
                  checked={formData.vehicle_registration_confirmed}
                  onCheckedChange={(checked) => { handleInputChange('vehicle_registration_confirmed', checked); clearFieldError('vehicle_registration_confirmed'); }}
                />
                <Label htmlFor="attest2" className={`text-sm leading-relaxed ${getFieldError('vehicle_registration_confirmed') ? 'text-red-700' : 'text-gray-700'}`}>
                  I confirm that the vehicle is registered and principally garaged as stated above.
                </Label>
              </div>
              
              <div className={`flex items-start space-x-3 ${getFieldError('state_notices_acknowledged') ? 'p-3 rounded-lg bg-red-50 border border-red-200' : ''}`}>
                <Checkbox
                  id="attest3"
                  data-testid="attest-state-checkbox"
                  checked={formData.state_notices_acknowledged}
                  onCheckedChange={(checked) => { handleInputChange('state_notices_acknowledged', checked); clearFieldError('state_notices_acknowledged'); }}
                />
                <Label htmlFor="attest3" className={`text-sm leading-relaxed ${getFieldError('state_notices_acknowledged') ? 'text-red-700' : 'text-gray-700'}`}>
                  I understand that coverage availability, notices, and disclosures may vary by state.
                </Label>
              </div>
              
              <div className={`flex items-start space-x-3 ${getFieldError('inaccurate_info_acknowledged') ? 'p-3 rounded-lg bg-red-50 border border-red-200' : ''}`}>
                <Checkbox
                  id="attest4"
                  data-testid="attest-inaccurate-checkbox"
                  checked={formData.inaccurate_info_acknowledged}
                  onCheckedChange={(checked) => { handleInputChange('inaccurate_info_acknowledged', checked); clearFieldError('inaccurate_info_acknowledged'); }}
                />
                <Label htmlFor="attest4" className={`text-sm leading-relaxed ${getFieldError('inaccurate_info_acknowledged') ? 'text-red-700' : 'text-gray-700'}`}>
                  I understand that providing inaccurate or incomplete information may affect eligibility, policy issuance, or claim payment.
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* State-Specific Surplus Lines Disclosure (Florida/Texas) */}
          {surplusLinesDisclosure && (
            <Card className="mb-6 border-amber-300 bg-amber-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-amber-800 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  {surplusLinesDisclosure.state_name} Surplus Lines Notice
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-amber-900 leading-relaxed" data-testid="surplus-lines-disclosure">
                  {surplusLinesDisclosure.disclosure_text}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Submit Button */}
          <div className="flex justify-center">
            <Button
              type="submit"
              data-testid="submit-info-btn"
              disabled={loading || ageError || formData.is_business_use || !formData.info_accurate_confirmed || !formData.vehicle_registration_confirmed || !formData.state_notices_acknowledged || !formData.inaccurate_info_acknowledged}
              className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-3 text-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-5 w-5" />
                  Continue to Issue Policy
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-500 leading-relaxed max-w-2xl mx-auto mb-4">
            LossPay Risk Technologies, Inc., doing business as LossPay Insurance Agency and LossPay, 
            operates as a licensed insurance agency and managing general agency arranging coverage with duly licensed insurers.
          </p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <a 
              href="https://losspay.com/StateNotices"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#3B5BDB] hover:text-[#2F4BC7] transition-colors"
            >
              State Notices
            </a>
            <a 
              href="https://losspay.com/PrivacyPolicy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#3B5BDB] hover:text-[#2F4BC7] transition-colors"
            >
              Privacy Policy
            </a>
            <a 
              href="https://losspay.com/TermsOfUse"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#3B5BDB] hover:text-[#2F4BC7] transition-colors"
            >
              Terms of Service
            </a>
          </div>
        </footer>
      </main>
    </div>
  );
}
